import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, LogOut, Menu, PhoneCall, UsersRound } from 'lucide-react';
import { useAuth, useLogout } from '../../features/auth/useAuth';
import { usePermissions } from '../../features/auth/usePermissions';
import { FilialPicker } from '../../features/multi-tenant/components/FilialPicker';
import { Avatar } from '../../features/design-system/components';
import { ImpersonatePicker } from '../../features/auth/components/ImpersonatePicker';
import { ImpersonationBanner } from '../../features/auth/components/ImpersonationBanner';
import { ZamerMestaPriVhode } from '../../features/auth/ZamerMestaPriVhode';
import { roleLabel } from '../../features/auth/roleLabels';
import { ShapkaPerepiski } from '../../features/obrashcheniya/ShapkaPerepiski';
import { StolHeaderButton } from '../../features/stol/StolHeaderButton';
import { SupportHelpButton } from '../../features/support/SupportHelpButton';
import { AccountSwitcher } from './AccountSwitcher';
import { ShapkaKolokol } from './ShapkaKolokol';
import { useSzhatieShapki } from './useSzhatieShapki';
import { Sidebar } from './Sidebar';
import { useSvernutoeMenyu } from './useSvernutoeMenyu';
import { RazdelyProvider } from './RazdelyContext';
// Стиль-ядро каркаса (Фаза 2): переменные объявлены под `.ops-skin`, на :root НЕ текут (§24).
import '../../features/design-system/tokens.css';

interface Props {
  children: ReactNode;
  messengerHeader?: boolean;
}

// Переключатель аккаунта (Beauty Medical / Лэш СПб) пока скрыт — мультиаккаунт заработает позже.
// Код НЕ удаляем: вернуть = поставить true. Решение владельца 26.06.
const ACCOUNT_SWITCHER_ENABLED = false;

// Глобальный филиал-селектор скрыт из столбца сайдбара: Пульс/Мастера/Клиенты имеют свои
// переключатели, аналитика на глобальный filial не опирается. Код НЕ удаляем: вернуть = true
// (или перенести в /profile). Решение владельца 28.06.
const FILIAL_PICKER_ENABLED = false;

/**
 * Telegram Web App режим: вход `?twa=1` отдаёт экран БЕЗ хрома админки
 * (сайдбар/шапка) под полную ширину webview. Флаг липкий на сессию — внутренняя
 * SPA-навигация теряет query, поэтому первый `?twa=1` запоминаем в sessionStorage,
 * чтобы стол остался chromeless при переходах. Сама initData-авторизация webview — в 2b.
 */
function useTwaMode(): boolean {
  const { search } = useLocation();
  const [twa, setTwa] = useState(() => {
    try {
      return sessionStorage.getItem('opsmind_twa') === '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    if (new URLSearchParams(search).get('twa') === '1') {
      try {
        sessionStorage.setItem('opsmind_twa', '1');
      } catch {
        /* private mode */
      }
      // Синхронизация флага из URL (липкий twa-режим) — намеренный setState в эффекте.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setTwa(true);
    }
  }, [search]);
  return twa;
}

/**
 * Каркас админки (Фаза 2 редизайна) на стиль-ядре `.ops-skin`: тёмный navy-сайдбар +
 * тонкая верхняя панель (уведомления, на мобильном — бургер). Профиль (имя/роль) + выход
 * + филиал — в подвале сайдбара. `.ops-skin` живёт ТОЛЬКО на хроме (сайдбар + топбар);
 * контент-зона (`<main>`) — вне слоя, поэтому ещё не переехавшие экраны рендерятся
 * байт-в-байт как раньше (§24, без типографической протечки base-слоя). Переехавшие
 * экраны (Пульс) сами оборачивают свой контент в `.ops-skin`.
 */
export function AppShell({ children, messengerHeader = false }: Props) {
  const { user } = useAuth();
  const { isSuperAdmin } = usePermissions();
  const logout = useLogout();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const twa = useTwaMode();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // `s698`: свёрнутое главное меню — выбор человека, живёт между переходами и перезагрузками.
  const [menyuSvernuto, pereklyuchitMenyu] = useSvernutoeMenyu();
  /* ⛔ `s329` Д11: телебар — полка с телефонией и сообщениями, открывается значком в шапке телефона. */
  const [telebar, setTelebar] = useState(false);
  // №221: выбор действует на переходах между журналом и карточкой в этой вкладке.
  const [shapkaSvernuta, setShapkaSvernuta] = useState(() => {
    try { return sessionStorage.getItem('opsmind.shapka.svernuta') === '1'; }
    catch { return false; }
  });
  const pereklyuchitShapku = () => {
    const next = !shapkaSvernuta;
    setShapkaSvernuta(next);
    try { sessionStorage.setItem('opsmind.shapka.svernuta', next ? '1' : '0'); }
    catch { /* Недоступное хранилище не мешает сворачиванию. */ }
  };
  // Переключатель «войти как пользователь» — только у владельца (users.impersonate)
  // и только пока он под собой: под чужой личиной вместо него полоса возврата.
  const [impersonateOpen, setImpersonateOpen] = useState(false);
  const canImpersonate = user?.can_impersonate === true;
  const impersonating = Boolean(user?.impersonated_by);
  // `s960` (владелец 21.10.2026): мастеру — только его журнал; филиал у него один, тот, где он работает.
  const tolkoMaster = user?.roles?.length === 1 && user.roles[0] === 'master';

  // header1: одна строка на компьютере — второстепенное сворачивается по шагам, текст не мельчает.
  const rowRef = useRef<HTMLElement | null>(null);
  const uroven = useSzhatieShapki(rowRef);

  // Закрывать выезжающую шторку при смене маршрута (на случай навигации не из неё) — намеренно.
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => setDrawerOpen(false), [pathname]);

  const onLogout = async () => {
    await logout.mutateAsync();
    navigate('/login', { replace: true });
  };

  // Подвал сайдбара: филиал (рабочий) + профиль (имя/роль) + выход. Рендерится ВНУТРИ
  // навигационного `<aside>` (он `.ops-skin`), поэтому ops-классы здесь разрешаются.
  const sidebarFooter = (
    <div className="space-y-2">
      {/* ⛔ БЛОК ТЕЛЕФОНИИ УЕХАЛ В ВЕРХНЮЮ ПАНЕЛЬ (владелец, 09.09.2026, `s311`):
          «зарезервировать там места для блока телефонии — текущий статус, сигнал
          звонка, быстрый переход на набор (то что сейчас слева внизу)». Внизу
          левой колонки он жил под пунктами меню — то есть под прокруткой: на
          длинном разделе статус уезжал за край, а он обязан быть на глазах
          всегда. Правило «виден на ЛЮБОЙ странице» (ТЗ §4.0) не тронуто —
          наверху оно выполняется строже. */}
      {FILIAL_PICKER_ENABLED && !tolkoMaster && (
        <div className="px-1">
          <FilialPicker />
        </div>
      )}
      {ACCOUNT_SWITCHER_ENABLED && (
        <div className="px-1">
          <AccountSwitcher />
        </div>
      )}
      <div className="flex items-center gap-2">
        <NavLink
          to="/profile"
          onClick={() => setDrawerOpen(false)}
          className={({ isActive }) =>
            [
              'flex min-w-0 flex-1 items-center gap-2.5 rounded-ops-md px-2 py-1.5 transition-colors duration-ops ease-ops',
              isActive ? 'bg-ops-sidebar-active-bg' : 'hover:bg-white/5',
            ].join(' ')
          }
        >
          <Avatar name={user?.name ?? 'Профиль'} src={user?.avatar_url ?? undefined} size="sm" />
          <span className="min-w-0 flex-1 leading-tight">
            <span className="block truncate text-ops-label font-semibold text-white">{user?.name ?? 'Профиль'}</span>
            <span className="block truncate text-ops-micro text-ops-sidebar-text-muted">
              {roleLabel(user?.roles, isSuperAdmin)}
            </span>
          </span>
        </NavLink>
        {canImpersonate && (
          <button
            type="button"
            onClick={() => setImpersonateOpen(true)}
            aria-label="Войти как пользователь"
            title="Войти как пользователь"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-ops-md text-ops-sidebar-text-muted transition-colors hover:bg-white/5 hover:text-white"
          >
            <UsersRound size={18} />
          </button>
        )}
        <button
          type="button"
          onClick={onLogout}
          aria-label="Выйти"
          title="Выйти"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-ops-md text-ops-sidebar-text-muted transition-colors hover:bg-white/5 hover:text-white"
        >
          <LogOut size={18} />
        </button>
      </div>
    </div>
  );

  // Подвал свёрнутого меню (`s698`): те же профиль и выход, значками в столбик.
  const sidebarFooterSvernutyy = (
    <div className="flex shrink-0 flex-col items-center gap-1 border-t border-white/10 pt-2">
      <NavLink
        to="/profile"
        title={`${user?.name ?? 'Профиль'} · ${roleLabel(user?.roles, isSuperAdmin)}`}
        aria-label="Профиль"
        className={({ isActive }) =>
          [
            'flex h-10 w-10 items-center justify-center rounded-ops-md transition-colors duration-ops ease-ops',
            isActive ? 'bg-ops-sidebar-active-bg' : 'hover:bg-white/5',
          ].join(' ')
        }
      >
        <Avatar name={user?.name ?? 'Профиль'} src={user?.avatar_url ?? undefined} size="sm" />
      </NavLink>
      {canImpersonate && (
        <button
          type="button"
          onClick={() => setImpersonateOpen(true)}
          aria-label="Войти как пользователь"
          title="Войти как пользователь"
          className="flex h-9 w-9 items-center justify-center rounded-ops-md text-ops-sidebar-text-muted transition-colors hover:bg-white/5 hover:text-white"
        >
          <UsersRound size={18} />
        </button>
      )}
      <button
        type="button"
        onClick={onLogout}
        aria-label="Выйти"
        title="Выйти"
        className="flex h-9 w-9 items-center justify-center rounded-ops-md text-ops-sidebar-text-muted transition-colors hover:bg-white/5 hover:text-white"
      >
        <LogOut size={18} />
      </button>
    </div>
  );

  // Telegram Web App: голый экран под webview + safe-area, без сайдбара/шапки.
  if (twa) {
    return (
      <main
        className="min-h-screen overflow-auto bg-[#F5F6FA] p-4 text-[#1A1F2E]"
        style={{
          paddingTop: 'max(1rem, env(safe-area-inset-top))',
          paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
          paddingLeft: 'max(1rem, env(safe-area-inset-left))',
          paddingRight: 'max(1rem, env(safe-area-inset-right))',
        }}
      >
        {children}
      </main>
    );
  }

  return (
    <RazdelyProvider>
    {/*
      ⛔⛔ ВЫСОТА ОБОЛОЧКИ — ВИДИМАЯ ОБЛАСТЬ, А НЕ `100vh` (`s342`, пункт 1
      приёмки 22.09: «появилась лишняя строка под рядом с кнопками и датами»,
      снимок владельца 21:00, Chrome на телефоне). `100vh` у Chrome — высота
      окна БЕЗ адресной строки; пока строка на экране, оболочка длиннее
      видимого на её высоту, и всё, что прижато к низу, расходится с краем
      экрана ровно на эти ~53 точки. `h-dvh` — видимая высота (браузер без
      `dvh` остаётся на `h-screen`). Правило общее для панели, а не заплата
      журнала.
    */}
    <div className="flex h-screen h-dvh bg-[#F5F6FA] text-[#1A1F2E]">
      <Sidebar
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        footer={sidebarFooter}
        svernuto={menyuSvernuto}
        onPereklyuchit={pereklyuchitMenyu}
        footerSvernutyy={sidebarFooterSvernutyy}
      />
      <ImpersonatePicker open={impersonateOpen} onClose={() => setImpersonateOpen(false)} />
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {/* Режим просмотра встроен в обычную шапку: отдельной фиолетовой полосы больше
            нет. На телефоне текст уходит второй строкой, но аварийный возврат остаётся. */}
        {/*
          ⛔ ВЕРХНЯЯ ПАНЕЛЬ СТАЛА РАБОЧЕЙ (владелец, 09.09.2026, `s311`). Дословно:
          «у нас не задействована верхняя бар-меню, можно её сделать выше и
          зарезервировать там места для блока телефонии, блока с мессенджерами и
          иных важных данных, которые должны быть в радиусе 1 клика из любого
          раздела. так у нас и место появится впихнуть навигацию и лого и не
          мешать это в левом меню».

          Слева — знак системы и ВЫБОР РАЗДЕЛА (уехал сюда из левой колонки).
          Справа — то, что нужно из любого раздела: телефония, стол, уведомления.
          Левая колонка отдана текущему разделу: филиал, календарь и пункты.
        */}
        {/*
          ⛔⛔ ОБЩАЯ ШАПКА В ОДНУ СТРОКУ (header1, макет Юрия принят 04.10.2026 «ок давай так»).
          Слово владельца: «оперативный доступ к звонилке и перепискам - оповещения - плюс
          выбор филиала. логотип мешается видимо, убираем его вправо».

          Слева направо: филиал · статус сотрудника · телефон · «Звонилка ▾» | «Переписки ▾» ·
          Непрочитано · Без ответа · Отложено · колокольчик | логотип. Не выше 78 px на
          компьютере; отдельного ряда уведомлений о переписках больше нет. Линия, обзвон и
          очередь — в раскрываемом пульте. Кнопка стола владельца стоит после колокольчика:
          в макете её нет, но рабочий инструмент владельца не убираем (названо в отчёте).

          На телефоне строка — меню · филиал · приборы экрана · телебар · знак; приборы шапки
          уходят на полку телебара, идущий звонок остаётся на виду.
        */}
        <div className="ops-global-header" data-telebar={telebar ? '1' : ''} data-collapsed={shapkaSvernuta ? '1' : '0'}>
        <header
          id="ops-global-toolbar"
          hidden={shapkaSvernuta}
          ref={rowRef}
          className={[
            'ops-global-toolbar ops-skin',
            `hdr-l${uroven}`,
            impersonating ? 'ops-impersonation-header' : '',
          ].join(' ')}
        >
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="Открыть меню"
            className="hdr-burger -ml-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-ops-md text-ops-text-secondary transition-colors hover:bg-ops-surface-2 hover:text-ops-text md:hidden"
          >
            <Menu size={22} />
          </button>

          {/* 1. Филиал — только здесь (`s329` Д9, op-rules §31.8). */}
          {!tolkoMaster && (
            <div className="hdr-item hdr-filial">
              <FilialPicker vShapke />
            </div>
          )}

          {/* Приборы экрана на телефоне (`s329` Д11): шапка остаётся одна физически. */}
          <div id="shapka-slot" className="hdr-slot flex min-w-0 flex-1 items-center gap-1 [overflow-x:clip] md:hidden" />

          {impersonating && <div className="hdr-item hdr-impersonation"><ImpersonationBanner /></div>}

          {/* ⛔ ТЕЛЕБАР — один значок вместо приборов, только на телефоне (`s329` Д11). */}
          <button
            type="button"
            aria-label="Телефония и сообщения"
            aria-expanded={telebar}
            onClick={() => setTelebar((v) => ! v)}
            className="hdr-telebar flex h-9 w-9 shrink-0 items-center justify-center rounded-ops-md text-ops-text-secondary transition-colors hover:bg-ops-surface-2 hover:text-ops-text md:hidden"
          >
            <PhoneCall size={18} />
          </button>
          {/* `s737`: «Сообщить о проблеме» на телефоне — значком в строке, а не на полке телебара. */}
          <SupportHelpButton telefon />

          <div className="hdr-tools">
            {/* 2–4. Статус, телефон, «Звонилка ▾» — рисует пульт над маршрутами (PhoneProvider). */}
            <div id="phone-pult-slot" className="hdr-contents" />
            {/* 5–8. «Переписки ▾» и три очереди. */}
            <ShapkaPerepiski />
            {/* 9. Остальные уведомления. */}
            <ShapkaKolokol />
            {/* `s737`: «Сообщить о проблеме» — каждому сотруднику с любой страницы. */}
            <SupportHelpButton />
            {/* Стол (stl1): только владелец. */}
            <StolHeaderButton className="hdr-item hdr-btn hdr-icon hdr-stol" />
          </div>

          {/* 10. Компактный логотип справа. */}
          <span className="hdr-item hdr-sep hdr-logo-sep" aria-hidden />
          <span className="hdr-item hdr-logo">
            <img
              src="/logo-mark.png"
              alt=""
              aria-hidden="true"
              width={26}
              height={26}
              className="h-[26px] w-[26px] shrink-0 select-none"
              draggable={false}
            />
            <span className="hdr-logo-word">OpsMind</span>
          </span>
        </header>
        {/* На телефоне меню и выход из просмотра сотрудника доступны и при свёрнутых приборах. */}
        {shapkaSvernuta && (
          <div className="ops-skin flex min-h-12 items-center justify-between gap-2 py-1 pl-3 pr-14 md:hidden">
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              aria-label="Открыть меню"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-ops-md text-ops-text-secondary hover:bg-ops-surface-2 hover:text-ops-text"
            >
              <Menu size={22} />
            </button>
            {impersonating && <ImpersonationBanner compact />}
          </div>
        )}
        <button
          type="button"
          className="hdr-collapse"
          aria-label={shapkaSvernuta ? 'Развернуть верхнюю панель' : 'Свернуть верхнюю панель'}
          title={shapkaSvernuta ? 'Развернуть верхнюю панель' : 'Свернуть верхнюю панель'}
          aria-controls="ops-global-toolbar"
          aria-expanded={!shapkaSvernuta}
          onClick={pereklyuchitShapku}
        >
          {shapkaSvernuta ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
        </button>
        <ZamerMestaPriVhode userId={user?.id} podChuzhimVhodom={impersonating} />
        </div>

        {/* ⛔ ЭКРАН ЗАПИСИ ИДЁТ ОТ КРАЯ (владелец, 09.09.2026: «убрать лишний
            воздух»). Общий отступ контентной зоны складывался с отступом самого
            экрана — вместе полсотни точек по кругу, а журналу дорога каждая:
            в них не влезали ни колонки мастеров, ни чипы счёта дня. Прочие
            разделы отступ сохраняют: у них воздух по краям — часть вида. */}
        <main
          className={[
            'min-h-0 flex-1 overflow-auto',
            pathname.startsWith('/zapis') || pathname.startsWith('/remont') ? 'p-0' : 'p-4 md:p-6',
            messengerHeader ? 'messenger-main' : '',
          ].join(' ')}
        >
          {children}
        </main>
      </div>
    </div>
    </RazdelyProvider>
  );
}

